import type { ReactNode } from 'react';
import { cn } from '../../lib/cn';

export interface SegmentOption<T extends string> {
  value: T;
  label?: string;
  icon?: ReactNode;
  /** Required when the option shows only an icon. */
  ariaLabel?: string;
}

const APPEARANCE = {
  default: {
    group: 'border border-line bg-surface',
    selected: 'bg-accent text-accent-contrast',
    idle: 'text-fg-secondary hover:bg-hover hover:text-fg',
  },
  /** On the band. */
  band: {
    group: 'bg-band-raised',
    selected: 'bg-band-fg text-band',
    idle: 'text-band-fg-secondary hover:text-band-fg',
  },
} as const;

interface SegmentedControlProps<T extends string> {
  options: SegmentOption<T>[];
  value: T | null;
  onChange: (value: T) => void;
  ariaLabel: string;
  appearance?: keyof typeof APPEARANCE;
  size?: 'sm' | 'md';
}

/** A single-choice group of pill buttons (radiogroup semantics). */
export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  appearance = 'default',
  size = 'md',
}: SegmentedControlProps<T>) {
  const styles = APPEARANCE[appearance];
  return (
    <div role="radiogroup" aria-label={ariaLabel} className={cn('inline-flex max-w-full gap-0.5 overflow-x-auto rounded-full p-[3px]', styles.group)}>
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={option.ariaLabel}
            title={option.label ? undefined : option.ariaLabel}
            onClick={() => onChange(option.value)}
            className={cn(
              'inline-flex cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-full px-3 text-[13px] font-medium transition-colors',
              size === 'sm' ? 'h-7' : 'h-8',
              !option.label && (size === 'sm' ? 'w-7 px-0' : 'w-8 px-0'),
              selected ? styles.selected : styles.idle,
            )}
          >
            {option.icon}
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
